import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2 } from 'lucide-react'
import { TransactionForm, createEmptyItem } from './transaction-form'
import type { ItemRow } from './transaction-form'
import type { Profile, Category, Tag } from '@/lib/hooks'
import { todayString } from '@/lib/utils'
import { validateAndToast, createTransactionSchema } from '@/lib/schemas'

export type ParsedItem = {
  name: string
  amount: number
  category_id: string | null
  category_suggestion: string | null
}

export type ParsedReceiptData = {
  date: string
  store_name: string
  items: ParsedItem[]
  total_amount: number
  tax_amount: number
}

type ReceiptPreviewProps = {
  parsedData: ParsedReceiptData
  categories: Category[]
  profiles: Profile[]
  currentUserId: string
  tags?: Tag[]
  storeNameSuggestions?: string[]
  itemNameSuggestions?: string[]
  onSave: (data: {
    date: string
    store_name: string
    memo?: string
    items: { name: string; amount: number; category_id: string | null }[]
    total_amount: number
    tax_amount: number
    paid_by: string
    input_method: string
    scope: 'household' | 'personal'
    tag_ids?: string[]
  }) => void | Promise<void>
  onCancel: () => void
  onCreateTag?: (data: { name: string; color: string }) => void
}

function parsedItemsToItemRows(parsedItems: ParsedItem[]): ItemRow[] {
  if (parsedItems.length === 0) return [createEmptyItem()]
  return parsedItems.map((item) => ({
    id: crypto.randomUUID(),
    name: item.name,
    amount: item.amount,
    category_id: item.category_id,
  }))
}

export default function ReceiptPreview({
  parsedData,
  categories,
  profiles,
  currentUserId,
  tags,
  storeNameSuggestions,
  itemNameSuggestions,
  onSave,
  onCancel,
  onCreateTag,
}: ReceiptPreviewProps) {
  const [date, setDate] = useState(parsedData.date || todayString())
  const [storeName, setStoreName] = useState(parsedData.store_name || '')
  const [items, setItems] = useState<ItemRow[]>(() => parsedItemsToItemRows(parsedData.items))
  const [memo, setMemo] = useState('')
  const [paidBy, setPaidBy] = useState(currentUserId)
  const [scope, setScope] = useState<'household' | 'personal'>('household')
  const [taxAmount, setTaxAmount] = useState(parsedData.tax_amount ?? 0)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  const totalAmount = items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0) + taxAmount

  const handleSave = async () => {
    if (saving) return

    const validItems = items.filter((i) => i.name.trim() !== '')
    const effectivePaidBy = scope === 'personal' ? currentUserId : paidBy

    const validated = validateAndToast(createTransactionSchema, {
      paid_by: effectivePaidBy,
      date,
      store_name: storeName || undefined,
      memo: memo || undefined,
      total_amount: totalAmount,
      tax_amount: taxAmount,
      input_method: 'receipt',
      items: validItems.map((item) => ({
        name: item.name,
        amount: Number(item.amount) || 0,
        category_id: item.category_id || null,
      })),
      scope,
      tag_ids: selectedTagIds.length > 0 ? selectedTagIds : undefined,
    })

    if (!validated) return

    setSaving(true)
    try {
      await onSave({
        date,
        store_name: storeName,
        memo: memo || undefined,
        items: validItems.map((item) => ({
          name: item.name,
          amount: Number(item.amount) || 0,
          category_id: item.category_id || null,
        })),
        total_amount: totalAmount,
        tax_amount: taxAmount,
        paid_by: effectivePaidBy,
        input_method: 'receipt',
        scope,
        tag_ids: selectedTagIds.length > 0 ? selectedTagIds : undefined,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>レシート内容の確認</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <TransactionForm
          date={date}
          storeName={storeName}
          memo={memo}
          items={items}
          paidBy={paidBy}
          profiles={profiles}
          categories={categories}
          scope={scope}
          taxAmount={taxAmount}
          tags={tags}
          selectedTagIds={selectedTagIds}
          storeNameSuggestions={storeNameSuggestions}
          itemNameSuggestions={itemNameSuggestions}
          onDateChange={setDate}
          onStoreNameChange={setStoreName}
          onMemoChange={setMemo}
          onItemsChange={setItems}
          onPaidByChange={setPaidBy}
          onTaxAmountChange={setTaxAmount}
          onScopeChange={setScope}
          onTagIdsChange={setSelectedTagIds}
          onCreateTag={onCreateTag}
        />

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>
            キャンセル
          </Button>
          <Button
            type="button"
            className="flex-1"
            onClick={handleSave}
            disabled={saving || items.every((i) => !i.name)}
          >
            {saving ? <Loader2 className="size-4 animate-spin" /> : null}
            保存
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
